
import type { PromotionsConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/promotions-config'
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { Promotion } from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type { Route } from 'vue-router'
import type {
  DataTableColumn,
  DataTableOption,
  GuardResolveFunction,
  ToolsMessages,
} from '../../models'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { computed, defineComponent, getCurrentInstance, reactive, ref, watch } from 'vue'
import CrudList from '../../components/crud/crud-list.component.vue'
import DataTable from '../../components/crud/data-table.component.vue'
import DefaultImage from '../../components/shared/default-image.component.vue'
import { useInstanceToolConfig } from '../../composables/use-instance-tool-config'
import { useToolingList } from '../../composables/use-tooling-list'
import { mapActions } from '../../composables/utils'
import { AlignEnum, FeatureIdEnum, SortDirectionEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { ANALYTICS_CONSTS } from '../../store/analytics/analytics.consts'

export default defineComponent({
  name: 'PromotionsList',
  components: {
    CrudList,
    DataTable,
    DefaultImage,
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction) {
    this.contextWatchDisabled = true

    next()
  },
  setup(props, context: SetupContext) {
    const {
      proxy: { $epb, $store },
    } = getCurrentInstance()

    const messages: ToolsMessages['promotions'] = $epb.messages.tools.promotions

    const routes = ROUTER_CONSTS.routes[FeatureIdEnum.PROMOTION]

    const crudListRef = ref(null)
    const defaultUpdateDateSortableId = $epb.useShoppablePromotions
      ? 'updated_on'
      : 'meta.updateDate'

    const toolingList = useToolingList<Promotion>(props, context, {
      routes,
      messages,
      defaultSorting: {
        field: defaultUpdateDateSortableId,
        direction: SortDirectionEnum.DESCENDING,
      },
      useVisibleFiltersAsPivotColumns: true,
      enableActionsAvailable: true,
    })

    const { fetchStatsReport } = mapActions($store, ANALYTICS_CONSTS.moduleName, {
      fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
    })

    const { toolConfig } = useInstanceToolConfig<PromotionsConfig>()

    toolingList.loadData()

    const populatedTableData = ref(toolingList.tableData.value)

    const columns = computed<Array<DataTableColumn>>(() => {
      const columns = [
        $epb.useShoppablePromotions
          ? {
              id: 'name',
              text: messages.fields.name.label,
              sortable: true,
              width: '250px',
              copyToClipboard: true,
            }
          : {
              id: 'setting.title',
              text: messages.aside.title.label,
              sortable: true,
              sortableId: 'title',
              width: '250px',
              copyToClipboard: true,
            },
        {
          id: 'criteria.queries',
          text: messages.fields.keywords.label,
          copyToClipboard: true,
          unwrapText: true,
        },
        ...toolingList.visiblePivotColumns.value,
        {
          id: 'setting.position',
          text: '#',
          width: '60px',
        },
        {
          id: 'setting.clicks',
          text: messages.fields.clicks.label,
          width: '60px',
          align: AlignEnum.CENTER,
        },
        {
          id: 'setting.imageUrl',
          text: messages.aside.imageUrl.label,
          width: '120px',
        },
        {
          id: 'schedule',
          text: messages.fields.schedule.label,
        },
        {
          id: 'metadata.updated',
          text: messages.fields.updateDate.label,
          sortable: true,
          sortableId: defaultUpdateDateSortableId,
        },
        {
          id: 'metadata.author',
          text: messages.fields.user.label,
        },
        {
          id: 'criteria.enabled',
          text: messages.fields.enabled.label,
          sortable: true,
          sortableId: 'enabled',
        },
        {
          id: 'options',
          width: '44px',
          align: AlignEnum.CENTER,
        },
      ]

      if (Array.isArray(toolConfig.value.types) && toolConfig.value.types.length > 1) {
        columns.unshift({
          id: 'setting.type',
          text: messages.aside.type.label,
          sortable: true,
          width: '8rem',
        })
      }

      return columns
    })

    function onOptionClick(option: DataTableOption, dataItem): void {
      toolingList.onOptionClick(option, dataItem, crudListRef)
    }

    function getTypeText(type: Promotion['setting']['type']): string {
      return messages.aside.type.values[type]
    }

    const clicksPerId = reactive<Record<string, number>>({})

    async function populateClicks(item: any): Promise<number> {
      try {
        const startDate = new Date(item.metadata.created)
        startDate.setUTCHours(0, 0, 0, 0)
        const topProductsResponse: StatsResponse = await fetchStatsReport({
          id: StatsReportsIdsEnum.TOP_PRODUCTS,
          params: {
            productIds: [item.id],
            startDate,
            endDate: new Date(),
          },
        })
        return topProductsResponse?.data?.[0]?.clickCount ?? 0
      } catch (e) {
        return 0
      }
    }

    watch(toolingList.tableData, async tableData => {
      populatedTableData.value = await Promise.all(
        tableData.map(async (dataItem: any) => {
          const clicks = await populateClicks(dataItem)
          return {
            ...dataItem,
            setting: {
              ...dataItem.setting,
              clicks,
            },
          }
        }),
      )
    })

    return {
      ...toolingList,
      populatedTableData,
      baseClass: 'promotions-list',
      crudListRef,
      columns,
      messages,
      routes,
      clicksPerId,
      onOptionClick,
      getTypeText,
    }
  },
})
